import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import { PRODUCTS, BRANDS, CATEGORIES, FITS } from "../mock/mock";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const brand = params.get("brand") || "all";
  const category = params.get("category") || "All";
  const fit = params.get("fit") || "All";
  const sale = params.get("sale") === "1";

  const setParam = (key, value, defaultVal) => {
    const next = new URLSearchParams(params);
    if (value === defaultVal) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    return PRODUCTS.filter((p) => {
      if (brand !== "all" && p.brandId !== brand) return false;
      if (category !== "All" && p.category !== category) return false;
      if (fit !== "All" && p.fit !== fit) return false;
      if (sale && !p.sale) return false;
      return true;
    });
  }, [brand, category, fit, sale]);
  // PRODUCTS is a module-level constant; filters above are the only dynamic inputs.

  return (
    <main className="pt-16 md:pt-[72px]">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 pt-12 md:pt-16 pb-8">
        <h1 className="font-display text-5xl md:text-7xl text-white">
          {sale ? "The Trash Pile" : "The Shop"}
        </h1>
        <p className="mt-3 text-white/60 max-w-xl">
          {sale
            ? "Marked-down pieces from past capsules. Grab 'em cheap while they last."
            : "Zero-inventory drops. Small themed capsules every 4–6 weeks. Grab yours while it's live."}
        </p>
      </section>

      <Marquee dark />

      {/* Filters */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 space-y-5">
        <div className="flex flex-wrap gap-2">
          {BRANDS.map((b) => (
            <Chip key={b.id} active={brand === b.id} onClick={() => setParam("brand", b.id, "all")}>
              {b.label}
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <Chip key={c} small active={category === c} onClick={() => setParam("category", c, "All")}>
              {c}
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-[0.18em] text-white/45">Fit:</span>
          {FITS.map((f) => (
            <Chip key={f} small active={fit === f} onClick={() => setParam("fit", f, "All")}>
              {f}
            </Chip>
          ))}
          <span className="ml-auto text-sm text-white/55">
            {filtered.length} piece{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 pb-20">
        {filtered.length === 0 ? (
          <div className="py-24 text-center text-white/50">
            No pieces match these filters. Try clearing a few.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filtered.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function Chip({ active, onClick, children, small }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border font-semibold uppercase tracking-[0.1em] transition ${
        small ? "text-[11px] px-3.5 py-1.5" : "text-xs px-4 py-2"
      } ${
        active
          ? "bg-neon text-black border-neon"
          : "bg-transparent text-white/70 border-white/15 hover:border-white/40 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}
